import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS } from './BlogData';

function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find(p => p.slug === slug);

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!post) {
        return (
            <div className="fade-in" style={{ textAlign: 'center', padding: '4rem 0' }}>
                <h2>Entry Not Found</h2>
                <Link to="/journal" className="text-btn">Return to Logs</Link>
            </div>
        );
    }

    return (
        <div className="fade-in">
            <Helmet>
                <title>{post.title} | Mindful Journal Prompts</title>
                <meta name="description" content={post.excerpt} />
                <link rel="canonical" href={`https://www.mindfuljournalprompts.com/journal/${post.slug}`} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={`${post.title} | Mindful Journal Prompts`} />
                <meta property="og:description" content={post.excerpt} />
                <meta property="og:url" content={`https://www.mindfuljournalprompts.com/journal/${post.slug}`} />
                <meta property="og:site_name" content="Mindful Journal Prompts" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${post.title} | Mindful Journal Prompts`} />
                <meta name="twitter:description" content={post.excerpt} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": post.title,
                        "description": post.excerpt,
                        "datePublished": post.date,
                        "url": `https://www.mindfuljournalprompts.com/journal/${post.slug}`,
                        "publisher": {
                            "@type": "Organization",
                            "name": "Mindful Journal Prompts"
                        }
                    })}
                </script>
            </Helmet>

            <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', textAlign: 'left' }}>
                <Link to="/journal" className="text-btn" style={{ marginBottom: '2rem', display: 'inline-block' }}>← Back to Logs</Link>

                <span className="meta" style={{ display: 'block', marginBottom: '1rem' }}>{post.date}</span>
                <h1 className="prompt-text" style={{ fontSize: '2.2rem', lineHeight: 1.3, marginBottom: '2rem', textAlign: 'left' }}>
                    {post.title}
                </h1>

                <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                    style={{
                        fontFamily: 'var(--sans)',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.8,
                        fontSize: '1.1rem'
                    }}
                />

                {post.relatedLink && (
                    <div style={{
                        marginTop: '4rem',
                        padding: '2rem',
                        background: 'rgba(0,0,0,0.03)',
                        borderRadius: '12px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: 'var(--text-main)' }}>Put this into practice</h3>
                        <Link to={post.relatedLink}>
                            <button className="gen-btn">{post.relatedLinkText}</button>
                        </Link>
                    </div>
                )}
            </div>

            <style>{`
        .blog-content h2 {
            font-size: 1.5rem;
            color: var(--text-main);
            margin-top: 2.5rem;
            margin-bottom: 1rem;
            font-weight: 500;
        }
        .blog-content p {
            margin-bottom: 1.5rem;
        }
        .blog-content h3 {
            font-size: 1.25rem;
            color: var(--text-main);
            margin-top: 2rem;
            margin-bottom: 0.75rem;
            font-weight: 500;
        }
        .blog-content ul, .blog-content ol {
            margin-bottom: 1.5rem;
            padding-left: 1.5rem;
            color: var(--text-secondary);
        }
        .blog-content li {
            margin-bottom: 0.5rem;
            line-height: 1.7;
        }
        .blog-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 1.5rem 0;
            font-size: 0.95rem;
            border-radius: 8px;
            overflow: hidden;
        }
        .blog-content table th,
        .blog-content table td {
            padding: 12px 16px;
            border: 1px solid rgba(0,0,0,0.1);
            text-align: left;
            color: var(--text-secondary);
        }
        .blog-content table th {
            background: rgba(0,0,0,0.04);
            color: var(--text-main);
            font-weight: 600;
            font-size: 0.9rem;
        }
        .blog-content table tr:hover {
            background: rgba(0,0,0,0.02);
        }
        .blog-content img {
            max-width: 100%;
            height: auto;
            border-radius: 12px;
            margin: 1rem 0;
        }
        .blog-content blockquote {
            border-left: 3px solid var(--accent);
            padding: 1rem 1.5rem;
            margin: 1.5rem 0;
            background: rgba(0,0,0,0.02);
            border-radius: 0 8px 8px 0;
            color: var(--text-secondary);
            font-style: italic;
        }
        .blog-content a {
            color: var(--text-main);
            text-decoration: underline;
            text-decoration-color: rgba(0,0,0,0.2);
            text-underline-offset: 2px;
            transition: text-decoration-color 0.2s;
        }
        .blog-content a:hover {
            text-decoration-color: var(--text-main);
        }
        .blog-content .stat-card {
            border-radius: 12px;
            padding: 24px;
            margin: 2rem 0;
        }
        @media (max-width: 600px) {
            .blog-content table {
                font-size: 0.85rem;
                display: block;
                overflow-x: auto;
            }
            .blog-content table th,
            .blog-content table td {
                padding: 8px 10px;
            }
        }
      `}</style>
        </div>
    );
}

export default BlogPost;
